// Map information bar + crosshair + current-location icon settings for the main /map.
//
// Mirrors the "Customise map information bar" screen in the iOS/Android apps. Token names follow
// the Android/server vocabulary (Kotlin enum names) so a future cloud sync can share keys:
//   slots      Empty | Coordinates | Elevation | Distance | BearingToMapCenter | Accuracy
//   icon size  Small | Medium | Large
//   styles     1..6 (positional, matches iOS style1..6 / Android ic_*_one..six)
//   colour     index 0..4 into CROSSHAIR_COLORS
//
// Everything here is device-local (localStorage), exactly as in the apps. The one setting the apps
// DO cloud-sync is the coordinate system (`User.gridReference`) — that lives in
// helpers/GridRefPreference.ts, not here.
//
// This module must stay free of mapbox-gl imports so its Jest suite runs without WebGL/TextDecoder.

export const MAP_INFO_BAR_SETTINGS_KEY = 'hiiker_map_info_bar_settings';

export type InfoBarSlot =
  | 'Empty'
  | 'Coordinates'
  | 'Elevation'
  | 'Distance'
  | 'BearingToMapCenter'
  | 'Accuracy';

export const INFO_BAR_SLOT_OPTIONS: ReadonlyArray<{ value: InfoBarSlot; label: string; group: string }> = [
  { value: 'Empty', label: 'Empty', group: 'None' },
  { value: 'Coordinates', label: 'Coordinates', group: 'Map info' },
  { value: 'Elevation', label: 'Elevation', group: 'Map info' },
  { value: 'Distance', label: 'Distance from me', group: 'Navigation' },
  { value: 'BearingToMapCenter', label: 'Bearing from me', group: 'Navigation' },
  { value: 'Accuracy', label: 'GPS accuracy', group: 'Navigation' },
];

export type IconSize = 'Small' | 'Medium' | 'Large';
export const ICON_SIZE_OPTIONS: ReadonlyArray<IconSize> = ['Small', 'Medium', 'Large'];

export type StyleIndex = 1 | 2 | 3 | 4 | 5 | 6;
export const STYLE_INDEXES: ReadonlyArray<StyleIndex> = [1, 2, 3, 4, 5, 6];

/** Order matches the apps' picker. Hex values follow iOS (Android's grey/purple differ slightly). */
export const CROSSHAIR_COLORS: ReadonlyArray<{ name: string; hex: string }> = [
  { name: 'Dark', hex: '#212228' },
  { name: 'Red', hex: '#DB2B35' },
  { name: 'Orange', hex: '#F57700' },
  { name: 'Yellow', hex: '#FDDF00' },
  { name: 'Purple', hex: '#9E00FF' },
];
export type ColorIndex = 0 | 1 | 2 | 3 | 4;

export interface MapInfoBarSettings {
  slots: [InfoBarSlot, InfoBarSlot, InfoBarSlot];
  showMiniCompass: boolean;
  crosshairEnabled: boolean;
  crosshairStyle: StyleIndex;
  crosshairColor: ColorIndex;
  locationIconStyle: StyleIndex;
  locationIconSize: IconSize;
  hideLocationMarker: boolean;
}

export const DEFAULT_MAP_INFO_BAR_SETTINGS: MapInfoBarSettings = {
  slots: ['Coordinates', 'Elevation', 'Distance'],
  showMiniCompass: false,
  crosshairEnabled: false,
  crosshairStyle: 1,
  crosshairColor: 0,
  locationIconStyle: 1,
  locationIconSize: 'Small',
  hideLocationMarker: false,
};

const SLOT_VALUES = new Set<string>(INFO_BAR_SLOT_OPTIONS.map((o) => o.value));

function isStyleIndex(v: unknown): v is StyleIndex {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 6;
}
function isColorIndex(v: unknown): v is ColorIndex {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < CROSSHAIR_COLORS.length;
}

/**
 * Merge an untrusted blob (localStorage, possibly from an older/newer build) onto the defaults.
 * Unknown or malformed values are dropped field-by-field so a bad blob can never wedge the bar.
 */
export function sanitizeMapInfoBarSettings(raw: unknown): MapInfoBarSettings {
  const out: MapInfoBarSettings = {
    ...DEFAULT_MAP_INFO_BAR_SETTINGS,
    slots: [...DEFAULT_MAP_INFO_BAR_SETTINGS.slots] as MapInfoBarSettings['slots'],
  };
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;

  if (Array.isArray(r.slots)) {
    for (let i = 0; i < 3; i += 1) {
      const v = r.slots[i];
      if (typeof v === 'string' && SLOT_VALUES.has(v)) out.slots[i] = v as InfoBarSlot;
    }
  }
  if (typeof r.showMiniCompass === 'boolean') out.showMiniCompass = r.showMiniCompass;
  if (typeof r.crosshairEnabled === 'boolean') out.crosshairEnabled = r.crosshairEnabled;
  if (isStyleIndex(r.crosshairStyle)) out.crosshairStyle = r.crosshairStyle;
  if (isColorIndex(r.crosshairColor)) out.crosshairColor = r.crosshairColor;
  if (isStyleIndex(r.locationIconStyle)) out.locationIconStyle = r.locationIconStyle;
  if (typeof r.locationIconSize === 'string' && (ICON_SIZE_OPTIONS as ReadonlyArray<string>).includes(r.locationIconSize)) {
    out.locationIconSize = r.locationIconSize as IconSize;
  }
  if (typeof r.hideLocationMarker === 'boolean') out.hideLocationMarker = r.hideLocationMarker;
  return out;
}

export function getMapInfoBarSettings(): MapInfoBarSettings {
  try {
    const raw = localStorage.getItem(MAP_INFO_BAR_SETTINGS_KEY);
    return sanitizeMapInfoBarSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return sanitizeMapInfoBarSettings(null);
  }
}

export function saveMapInfoBarSettings(settings: MapInfoBarSettings): void {
  try {
    localStorage.setItem(MAP_INFO_BAR_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* private mode / quota: settings simply don't persist */
  }
}

// ─── Focus point ─────────────────────────────────────────────────────────────

export interface LngLatLike { lng: number; lat: number }

/**
 * Which point the bar describes. With the crosshair on it is always the map centre (the crosshair
 * sits there); off, it follows the mouse, falling back to the centre until the mouse has entered.
 */
export function resolveFocusPoint(
  crosshairEnabled: boolean,
  center: LngLatLike | null,
  mouse: LngLatLike | null,
): LngLatLike | null {
  if (crosshairEnabled) return center;
  return mouse ?? center;
}

// ─── Slot formatting ─────────────────────────────────────────────────────────

export interface UserFix extends LngLatLike { accuracy: number | null }

export const NO_VALUE = '–';
const EARTH_RADIUS_M = 6371008.8;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in metres (haversine). */
export function distanceMeters(a: LngLatLike, b: LngLatLike): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Initial bearing from a to b, 0..360 clockwise from true north. */
export function bearingDegrees(a: LngLatLike, b: LngLatLike): number {
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δλ = toRad(b.lng - a.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
export function compassPoint(bearing: number): string {
  return COMPASS_POINTS[Math.round((((bearing % 360) + 360) % 360) / 45) % 8];
}

export function formatBearing(bearing: number): string {
  return `${Math.round(bearing) % 360}° ${compassPoint(bearing)}`;
}

/** Elevation strings from getElevation() arrive as "• 123 m"; the bar shows the bare value. */
export function stripElevationPrefix(elevation: string | null | undefined): string {
  if (!elevation) return NO_VALUE;
  const s = elevation.replace(/^\s*•\s*/, '').trim();
  return s.length ? s : NO_VALUE;
}

/** Minimal unit-formatter surface we need, so MeasurementPref stays mockable in tests. */
export interface DistanceFormatter {
  smallUnitFormatter(meters: number, convertToBigUnitAfter1000M?: Boolean): string;
}

export interface SlotInputs {
  focus: LngLatLike | null;
  coordinates: string | null;
  elevation: string | null;
  user: UserFix | null;
  units: DistanceFormatter;
}

export function formatSlotValue(slot: InfoBarSlot, inputs: SlotInputs): string {
  switch (slot) {
    case 'Empty':
      return '';
    case 'Coordinates':
      return inputs.coordinates && inputs.coordinates.length ? inputs.coordinates : NO_VALUE;
    case 'Elevation':
      return stripElevationPrefix(inputs.elevation);
    case 'Distance':
      if (!inputs.user || !inputs.focus) return NO_VALUE;
      return inputs.units.smallUnitFormatter(distanceMeters(inputs.user, inputs.focus), true);
    case 'BearingToMapCenter':
      if (!inputs.user || !inputs.focus) return NO_VALUE;
      return formatBearing(bearingDegrees(inputs.user, inputs.focus));
    case 'Accuracy':
      if (!inputs.user || inputs.user.accuracy == null) return NO_VALUE;
      return `± ${inputs.units.smallUnitFormatter(inputs.user.accuracy)}`;
    default:
      return NO_VALUE;
  }
}

export interface InfoBarSlotGroup { group: string; options: Array<{ value: InfoBarSlot; label: string; group: string }> }

/** Slot options bucketed by group, in first-seen order, for an <optgroup> select. */
export function groupedInfoBarSlotOptions(
  options: ReadonlyArray<{ value: InfoBarSlot; label: string; group: string }> = INFO_BAR_SLOT_OPTIONS,
): InfoBarSlotGroup[] {
  const groups: InfoBarSlotGroup[] = [];
  for (const opt of options) {
    let bucket = groups.find((g) => g.group === opt.group);
    if (!bucket) {
      bucket = { group: opt.group, options: [] };
      groups.push(bucket);
    }
    bucket.options.push(opt);
  }
  return groups;
}

export function slotLabel(slot: InfoBarSlot): string {
  return INFO_BAR_SLOT_OPTIONS.find((o) => o.value === slot)?.label ?? slot;
}

// ─── Compass ─────────────────────────────────────────────────────────────────

/** The needle counter-rotates the map bearing so it keeps pointing at true north. */
export function compassRotation(mapBearing: number): number {
  return -mapBearing;
}

// ─── Current-location icon → body classes ────────────────────────────────────

export const LOCATION_ICON_CLASS_PREFIX = 'hk-loc-icon-';
export const LOCATION_SIZE_CLASS_PREFIX = 'hk-loc-size-';
export const LOCATION_HIDDEN_CLASS = 'hk-loc-hidden';

export function locationIconBodyClasses(settings: Pick<MapInfoBarSettings, 'locationIconStyle' | 'locationIconSize' | 'hideLocationMarker'>): string[] {
  const classes = [
    `${LOCATION_ICON_CLASS_PREFIX}${settings.locationIconStyle}`,
    `${LOCATION_SIZE_CLASS_PREFIX}${settings.locationIconSize.toLowerCase()}`,
  ];
  if (settings.hideLocationMarker) classes.push(LOCATION_HIDDEN_CLASS);
  return classes;
}

/** Every class this module might have put on <body>, so stale ones can be cleared first. */
export function allLocationIconBodyClasses(): string[] {
  return [
    ...STYLE_INDEXES.map((i) => `${LOCATION_ICON_CLASS_PREFIX}${i}`),
    ...ICON_SIZE_OPTIONS.map((s) => `${LOCATION_SIZE_CLASS_PREFIX}${s.toLowerCase()}`),
    LOCATION_HIDDEN_CLASS,
  ];
}

export function applyLocationIconBodyClasses(body: { classList: DOMTokenList }, settings: MapInfoBarSettings): void {
  body.classList.remove(...allLocationIconBodyClasses());
  body.classList.add(...locationIconBodyClasses(settings));
}

// ─── Map interaction lock ("Disable touch screen" in the apps) ───────────────

export const MAP_INTERACTION_HANDLERS = [
  'dragPan', 'scrollZoom', 'boxZoom', 'dragRotate', 'doubleClickZoom', 'keyboard', 'touchZoomRotate', 'touchPitch',
] as const;

/** Enable/disable every mapbox interaction handler present on the map object. Session-only. */
export function setMapInteractionsLocked(map: Record<string, any> | null | undefined, locked: boolean): void {
  if (!map) return;
  for (const name of MAP_INTERACTION_HANDLERS) {
    const handler = map[name];
    if (!handler) continue;
    if (locked) handler.disable?.(); else handler.enable?.();
  }
}
